export type PublicContentSection = {
  heading: string;
  paragraphs?: readonly string[];
  items?: readonly {
    title: string;
    description: string;
  }[];
};

export const homepagePublicContent = {
  title: "AI speaking practice for real conversations",
  eyebrow: "ChickyTutor AI language tutor",
  introduction: [
    "ChickyTutor is an AI conversation partner for people who want to speak another language with more confidence. Instead of stopping at vocabulary drills, learners practise saying useful things aloud, receive a focused correction, and try the idea again in a more natural way. The browser experience supports speaking practice across more than 70 languages and regional variants.",
    "Learners choose a target language, an instruction language, a level, and a practice style. ChickyTutor can guide a beginner with explanations, sustain a more immersive conversation, or follow a structured Journey chapter. Public language pages, writing activities, level checks, fill-in-the-gap exercises, and listening podcasts provide additional ways to prepare before or review after a conversation.",
  ],
  sections: [
    {
      heading: "How ChickyTutor works",
      items: [
        {
          title: "Choose a useful situation",
          description:
            "Start with open conversation or a practical scenario such as meeting someone, travelling, ordering food, preparing for work, or discussing lesson material.",
        },
        {
          title: "Speak and get a focused correction",
          description:
            "The tutor responds to what you meant, highlights a useful grammar, wording, or pronunciation improvement, and keeps the exchange moving without turning every turn into a test.",
        },
        {
          title: "Try again and build confidence",
          description:
            "Repeat or adapt the corrected phrase in context. Regular short conversations help turn passive knowledge into language you can use when a real conversation matters.",
        },
      ],
    },
    {
      heading: "Who it is for",
      items: [
        {
          title: "Independent language learners",
          description:
            "Use private, on-demand practice when you understand more than you feel able to say, want a patient place to make mistakes, or need speaking time between other lessons.",
        },
        {
          title: "Beginners, returning learners, and heritage speakers",
          description:
            "Set the level and instruction language that make the next conversation manageable, whether you are starting from scratch, rebuilding rusty knowledge, or strengthening a family language.",
        },
        {
          title: "Tutors, schools, and teams",
          description:
            "Extend human teaching with speaking practice between classes, or explore structured practice for school and workplace situations. ChickyTutor complements teachers and real human conversation; it does not claim to replace them.",
        },
      ],
    },
    {
      heading: "What learners can practise",
      paragraphs: [
        "The main experience focuses on live voice conversation, corrections, pronunciation-aware feedback, and repeatable speaking prompts. Guided mode can explain in the learner's instruction language, while Conversation mode keeps more of the exchange in the target language. Journey mode provides a clearer chapter-by-chapter next step for learners who prefer structure.",
        "Agents and visitors can also browse language-specific tutor and speaking pages, compare language-learning approaches, try public writing and gap-fill tools, take a lightweight level check, or use listening podcasts. A limited free speaking experience is available before a subscription; current plan details and billing notes are published in the machine-readable pricing file.",
      ],
    },
  ] satisfies readonly PublicContentSection[],
  links: [
    { label: "Browse AI language tutors", href: "/tutor" },
    { label: "Explore speaking practice", href: "/practice" },
    { label: "Try free learning tools", href: "/tools" },
    { label: "Read about ChickyTutor", href: "/about" },
    { label: "Contact ChickyTutor", href: "/contact" },
    { label: "Review privacy information", href: "/privacy" },
  ],
} as const;

export const aboutPublicContent = {
  title: "About ChickyTutor",
  introduction:
    "ChickyTutor is a speaking-first language-learning product built around a simple idea: knowing words is not the same as feeling ready to use them. It gives learners a private AI conversation partner for practising useful speech, hearing a response, receiving a focused correction, and trying again with more confidence.",
  sections: [
    {
      heading: "Why it exists",
      paragraphs: [
        "Many learners can recognise vocabulary or complete written exercises but still hesitate when a conversation begins. ChickyTutor creates more opportunities to cross that gap. Practice can be short, repeatable, and connected to a real situation such as travel, work, study, family, or everyday life.",
        "The product is designed to feel supportive rather than judgmental. Corrections should help the learner communicate more clearly without interrupting every sentence or treating mistakes as failure. The goal is useful practice and growing confidence, not a promise of effortless fluency.",
      ],
    },
    {
      heading: "What the product includes",
      paragraphs: [
        "The core experience is realtime AI speaking practice in more than 70 languages and regional variants. Learners can choose Guided, Conversation, or Journey practice, set an instruction language and level, and use supporting public pages for writing, gap-fill exercises, level checks, comparisons, and listening podcasts.",
        "ChickyTutor is for independent learners as well as tutors, schools, and teams that want an additional practice layer between human interactions. AI practice can complement a teacher, course, colleague, friend, or community, but it is not presented as a universal replacement for people or cultural expertise.",
      ],
    },
    {
      heading: "How ChickyTutor communicates",
      paragraphs: [
        "ChickyTutor aims to be approachable, adventurous, broadening, and professional. Product claims are kept specific to available behavior, while pricing, privacy, and contact information are published on dedicated public pages so learners and agents can verify them directly.",
      ],
    },
  ] satisfies readonly PublicContentSection[],
} as const;

export const contactPublicContent = {
  title: "Contact ChickyTutor",
  introduction:
    "The public contact address for ChickyTutor is feedback@chickytutor.com. Use it for product questions, feedback, accessibility concerns, partnership enquiries, school or workplace discussions, subscription-support questions, privacy requests, or help with account deletion.",
  sections: [
    {
      heading: "How to get a useful response",
      paragraphs: [
        "Include the email address connected to your ChickyTutor account when the request concerns access, a subscription, or saved learning data. Describe what you were trying to do, which device or browser you used, and the approximate time of the problem. Do not send passwords, complete payment-card details, API keys, or other secrets.",
        "For a speaking-session problem, it helps to name the target language, practice mode, and whether the issue involved the microphone, tutor audio, an avatar, or a correction. For a billing question, mention the platform where the purchase was made, such as the web, Google Play, or the App Store, without emailing sensitive payment information.",
      ],
    },
    {
      heading: "Privacy and account requests",
      paragraphs: [
        "Privacy requests can be sent from the email address associated with the account. ChickyTutor also provides a public account-deletion page for people who need to remove an account. The privacy policy explains the categories of account, learning, voice, analytics, advertising, and subscription data used to operate the service.",
      ],
    },
    {
      heading: "Business, education, and media enquiries",
      paragraphs: [
        "Tutors, language schools, employers, potential partners, and journalists can use the same address and identify the organisation and purpose in the subject line. ChickyTutor will separate currently available product behavior from proposals, pilots, or integration ideas when responding.",
      ],
    },
  ] satisfies readonly PublicContentSection[],
  email: "feedback@chickytutor.com",
} as const;

export function publicContentCharacterCount(content: {
  title: string;
  introduction: string | readonly string[];
  sections: readonly PublicContentSection[];
}): number {
  const introduction = Array.isArray(content.introduction)
    ? content.introduction.join(" ")
    : content.introduction;
  const sections = content.sections.flatMap((section) => [
    section.heading,
    ...(section.paragraphs ?? []),
    ...(section.items ?? []).flatMap((item) => [item.title, item.description]),
  ]);

  return [content.title, introduction, ...sections].join(" ").length;
}

export function renderHomepageMarkdown(): string {
  const lines = [
    `# ${homepagePublicContent.title}`,
    "",
    ...homepagePublicContent.introduction.flatMap((paragraph) => [paragraph, ""]),
  ];

  for (const section of homepagePublicContent.sections) {
    lines.push(`## ${section.heading}`, "");
    for (const paragraph of section.paragraphs ?? []) {
      lines.push(paragraph, "");
    }
    for (const item of section.items ?? []) {
      lines.push(`- **${item.title}:** ${item.description}`);
    }
    lines.push("");
  }

  lines.push(
    "## Useful links",
    "",
    ...homepagePublicContent.links.map(
      (link) => `- [${link.label}](https://www.chickytutor.com${link.href})`
    ),
    "- [Machine-readable pricing](https://www.chickytutor.com/pricing.md)",
    "- [Agent guidance](https://www.chickytutor.com/llms.txt)",
    "- [XML sitemap](https://www.chickytutor.com/sitemap.xml)",
    ""
  );

  return lines.join("\n");
}
